import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Chip,
    FormControlLabel,
    Checkbox,
    LinearProgress,
    Divider,
    Tooltip
} from '@mui/material';
import {
    CloudDownload as ExportIcon,
    CloudUpload as ImportIcon,
    Backup as BackupIcon,
    Restore as RestoreIcon,
    Delete as DeleteIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    CheckCircle as SuccessIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import dataManagementService from '../../services/dataManagementService';
import useNotificationStore from '../../stores/notificationStore';
import BackupConfiguration from './components/BackupConfiguration';

const DataManagement = () => {
    const { showSuccess, showError, showWarning } = useNotificationStore();
    const [backups, setBackups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedBackup, setSelectedBackup] = useState(null);
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const [backupDialogOpen, setBackupDialogOpen] = useState(false);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [backupDescription, setBackupDescription] = useState('');
    const [importFile, setImportFile] = useState(null);
    const [importOptions, setImportOptions] = useState({
        clearExisting: false,
        skipValidation: false,
        updateExisting: false
    });
    const [operationProgress, setOperationProgress] = useState(null);
    const [progressInterval, setProgressInterval] = useState(null);

    useEffect(() => {
        loadBackups();
        
        // Cleanup interval on unmount
        return () => {
            if (progressInterval) {
                clearInterval(progressInterval);
            }
        };
    }, []);

    const loadBackups = async () => {
        try {
            setLoading(true);
            const response = await dataManagementService.listBackups();
            setBackups(response.backups || []);
        } catch (error) {
            showError('Failed to load backups');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            setLoading(true);
            await dataManagementService.exportData();
            showSuccess('Data exported successfully');
        } catch (error) {
            showError('Export failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const startProgressTracking = (operationId) => {
        const interval = setInterval(async () => {
            try {
                const progressData = await dataManagementService.getProgress(operationId);
                if (progressData.progress) {
                    setOperationProgress(progressData.progress);
                    
                    if (progressData.progress.status === 'completed' || progressData.progress.status === 'failed') {
                        clearInterval(interval);
                        setProgressInterval(null);
                        setTimeout(() => setOperationProgress(null), 3000);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch progress:', error);
            }
        }, 1000);
        
        setProgressInterval(interval);
    };

    const handleImport = async () => {
        if (!importFile) {
            showWarning('Please select a file to import');
            return;
        }

        try {
            setLoading(true);
            const result = await dataManagementService.importData(importFile, importOptions);
            
            if (result.success) {
                if (result.result.failed.length > 0) {
                    showWarning(`Import completed: ${result.result.success.length} successful, ${result.result.failed.length} failed`);
                } else {
                    showSuccess(`Import completed: ${result.result.success.length} records imported successfully`);
                }
                setImportDialogOpen(false);
                setImportFile(null);
            }
        } catch (error) {
            showError('Import failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateBackup = async () => {
        try {
            setLoading(true);
            const result = await dataManagementService.createBackup(backupDescription);
            
            showSuccess('Backup created successfully');
            setBackupDialogOpen(false);
            setBackupDescription('');
            loadBackups();
        } catch (error) {
            showError('Backup failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = async () => {
        if (!selectedBackup) return;

        try {
            setLoading(true);
            const result = await dataManagementService.restoreBackup(selectedBackup._id);
            
            showSuccess('Backup restored successfully');
            setRestoreDialogOpen(false);
        } catch (error) {
            showError('Restore failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedBackup) return;

        try {
            setLoading(true);
            await dataManagementService.deleteBackup(selectedBackup._id);
            showSuccess('Backup deleted successfully');
            setDeleteDialogOpen(false);
            loadBackups();
        } catch (error) {
            showError('Delete failed: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" gutterBottom>
                    Data Management
                </Typography>
                <Typography variant="body1" color="text.secondary" paragraph>
                    Export, import, backup and restore your system data. Only available to super administrators.
                </Typography>

                <Alert severity="warning" sx={{ mb: 3 }}>
                    <strong>Warning:</strong> These operations can significantly impact your system. 
                    Always create a backup before importing data or restoring from a previous backup.
                </Alert>

                {/* Progress Indicator */}
                {operationProgress && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Operation Progress
                        </Typography>
                        <LinearProgress 
                            variant="determinate" 
                            value={(operationProgress.processed / operationProgress.total) * 100}
                            sx={{ mb: 1 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {operationProgress.status === 'in_progress' ? 'Processing' : operationProgress.status}: 
                            {' '}{operationProgress.processed} / {operationProgress.total} items
                        </Typography>
                    </Paper>
                )}

                <Grid container spacing={3}>
                    {/* Quick Actions */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Quick Actions
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<ExportIcon />}
                                        onClick={handleExport}
                                        disabled={loading}
                                    >
                                        Export Data
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<ImportIcon />}
                                        onClick={() => setImportDialogOpen(true)}
                                        disabled={loading}
                                    >
                                        Import Data
                                    </Button>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<BackupIcon />}
                                        onClick={() => setBackupDialogOpen(true)}
                                        disabled={loading}
                                    >
                                        Create Backup
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>
                    </Grid>

                    {/* Backups List */}
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Available Backups
                            </Typography>
                            
                            {loading && <LinearProgress sx={{ mb: 2 }} />}
                            
                            {backups.length === 0 ? (
                                <Alert severity="info">
                                    No backups available. Create your first backup to get started.
                                </Alert>
                            ) : (
                                <List>
                                    {backups.map((backup) => (
                                        <React.Fragment key={backup._id}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Typography variant="subtitle1">
                                                                {backup.description || 'Backup'}
                                                            </Typography>
                                                            {backup.status === 'completed' && (
                                                                <Chip size="small" label="Ready" color="success" />
                                                            )}
                                                            {backup.status === 'pending' && (
                                                                <Chip size="small" label="Processing" color="warning" />
                                                            )}
                                                            {backup.status === 'failed' && (
                                                                <Chip size="small" label="Failed" color="error" />
                                                            )}
                                                        </Box>
                                                    }
                                                    secondary={
                                                        <Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Created: {format(new Date(backup.timestamp), 'PPpp')}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                Size: {formatFileSize(backup.size)} • 
                                                                Collections: {backup.collections?.length || 0}
                                                                {backup.s3Key && ' • ☁️ S3'}
                                                            </Typography>
                                                            {backup.createdBy && (
                                                                <Typography variant="body2" color="text.secondary">
                                                                    By: {backup.createdBy.name || backup.createdBy.email}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Restore from this backup">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => {
                                                                setSelectedBackup(backup);
                                                                setRestoreDialogOpen(true);
                                                            }}
                                                            disabled={loading || backup.status !== 'completed'}
                                                        >
                                                            <RestoreIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete this backup">
                                                        <IconButton
                                                            edge="end"
                                                            onClick={() => {
                                                                setSelectedBackup(backup);
                                                                setDeleteDialogOpen(true);
                                                            }}
                                                            disabled={loading}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            <Divider />
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}
                        </Paper>
                    </Grid>
                    
                    {/* Automatic Backup Configuration */}
                    <Grid item xs={12}>
                        <BackupConfiguration />
                    </Grid>
                </Grid>

                {/* Import Dialog */}
                <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Import Data</DialogTitle>
                    <DialogContent>
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Importing data will modify your database. Create a backup first!
                        </Alert>
                        
                        <Button
                            variant="outlined"
                            component="label"
                            fullWidth
                            sx={{ mb: 2 }}
                        >
                            {importFile ? importFile.name : 'Select JSON File'}
                            <input
                                type="file"
                                hidden
                                accept=".json"
                                onChange={(e) => setImportFile(e.target.files[0])}
                            />
                        </Button>
                        
                        <Typography variant="subtitle2" gutterBottom>
                            Import Options:
                        </Typography>
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={importOptions.clearExisting}
                                    onChange={(e) => setImportOptions({
                                        ...importOptions,
                                        clearExisting: e.target.checked
                                    })}
                                />
                            }
                            label="Clear existing data before import"
                        />
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={importOptions.updateExisting}
                                    onChange={(e) => setImportOptions({
                                        ...importOptions,
                                        updateExisting: e.target.checked
                                    })}
                                />
                            }
                            label="Update existing records"
                        />
                        
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={importOptions.skipValidation}
                                    onChange={(e) => setImportOptions({
                                        ...importOptions,
                                        skipValidation: e.target.checked
                                    })}
                                />
                            }
                            label="Skip validation (faster but risky)"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setImportDialogOpen(false)}>Cancel</Button>
                        <Button 
                            onClick={handleImport} 
                            variant="contained" 
                            disabled={!importFile || loading}
                        >
                            Import
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Backup Dialog */}
                <Dialog open={backupDialogOpen} onClose={() => setBackupDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Create Backup</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Description (optional)"
                            value={backupDescription}
                            onChange={(e) => setBackupDescription(e.target.value)}
                            margin="normal"
                            placeholder="e.g., Before major update"
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setBackupDialogOpen(false)}>Cancel</Button>
                        <Button 
                            onClick={handleCreateBackup} 
                            variant="contained" 
                            disabled={loading}
                        >
                            Create Backup
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Restore Dialog */}
                <Dialog open={restoreDialogOpen} onClose={() => setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Restore from Backup</DialogTitle>
                    <DialogContent>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <strong>Warning:</strong> This will replace ALL current data with the backup data. 
                            This action cannot be undone!
                        </Alert>
                        
                        {selectedBackup && (
                            <Box>
                                <Typography variant="body2">
                                    You are about to restore from:
                                </Typography>
                                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                                    {selectedBackup.description || 'Backup'} - 
                                    {format(new Date(selectedBackup.timestamp), 'PPpp')}
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRestoreDialogOpen(false)}>Cancel</Button>
                        <Button 
                            onClick={handleRestore} 
                            variant="contained" 
                            color="error"
                            disabled={loading}
                        >
                            Restore
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Delete Backup</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Are you sure you want to delete this backup? This action cannot be undone.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button 
                            onClick={handleDelete} 
                            variant="contained" 
                            color="error"
                            disabled={loading}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default DataManagement;